/**
 * Hook resolving the data needed to render a booking-type embed snippet.
 *
 * The embed snippet needs two independent pieces of server data:
 *   1. the booking type's shareable link (`…/booking-types/<id>/booking-link/`)
 *   2. the org's website tag slug     (`…/websites/tag/`, get-or-provision)
 *
 * Both are plain REST resources (NOT event-sourced CRM entities), so they go
 * through React Query per the "Pattern B" data-fetching rule, matching the
 * sibling `useBookingLinkBaseUrl` hook. React Query gives dedup, caching,
 * retry-on-demand, and unmount safety, so the section itself holds no manual
 * fetch/loading/error state.
 *
 * Fetching is lazy: nothing runs until `enabled` flips true (the section only
 * enables it on first expand). Auto-retry is disabled to preserve the
 * single-shot-then-manual-"Try again" behavior — `retry()` refetches whichever
 * query is in a retryable error state (including a 200-with-blank-slug tag).
 *
 * Derived status mirrors the original branch logic exactly:
 *   - booking-link 404 (or no usable link) → `unavailable` (calm, non-retryable)
 *   - tag failure / blank slug             → `error` (retryable)
 *   - both resolve with a usable link+slug → `ready`
 */

import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';

import { apiGet } from '@/lib/http/client';

import { buildGlobalSlugUrl, buildShortCodeUrl } from './buildBookingUrl';

interface BookingLinkResponse {
  short_code: string;
  global_slug?: string | null;
  share_base_url: string | null;
}

interface WebsiteTagResponse {
  slug: string;
  webhook_entity_id: string;
  provisioned: boolean;
}

export type EmbedCodeStatus =
  | 'idle'
  | 'loading'
  | 'ready'
  | 'error'
  | 'unavailable';

export interface EmbedCodeData {
  status: EmbedCodeStatus;
  /** Website tag slug — only populated when status is `ready`. */
  slug: string;
  /** Resolved public booking URL — only populated when status is `ready`. */
  bookingLink: string;
  /** Refetch both underlying queries (used by the "Try again" affordance). */
  retry: () => void;
}

export function useEmbedCodeData(
  orgId: number | null | undefined,
  entityId: string | null | undefined,
  enabled: boolean
): EmbedCodeData {
  const active = enabled && orgId != null && !!entityId;

  const linkQuery = useQuery({
    queryKey: ['scheduling-booking-link', orgId, entityId],
    queryFn: () =>
      apiGet<BookingLinkResponse>(
        `/api/orgs/${orgId}/scheduling/booking-types/${entityId}/booking-link/`
      ),
    enabled: active,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const tagQuery = useQuery({
    queryKey: ['websites-tag', orgId],
    queryFn: () =>
      apiGet<WebsiteTagResponse>(`/api/orgs/${orgId}/websites/tag/`),
    enabled: active,
    retry: false,
    refetchOnWindowFocus: false,
  });

  // A tag endpoint that returns 200 with an empty slug is treated as a
  // retryable `error` below (same as a hard tag failure), so "Try again" must
  // refetch it in that case too — otherwise the error state can never recover
  // because the query is technically successful and `tagQuery.isError` is false.
  const tagHasBlankSlug = tagQuery.isSuccess && !tagQuery.data.slug;

  const { isError: linkIsError, refetch: refetchLink } = linkQuery;
  const { isError: tagIsError, refetch: refetchTag } = tagQuery;

  const retry = useCallback(() => {
    if (linkIsError) {
      void refetchLink();
    }
    if (tagIsError || tagHasBlankSlug) {
      void refetchTag();
    }
  }, [linkIsError, refetchLink, tagIsError, tagHasBlankSlug, refetchTag]);

  if (!active) {
    return { status: 'idle', slug: '', bookingLink: '', retry };
  }

  const linkSettled = linkQuery.isSuccess || linkQuery.isError;
  const tagSettled = tagQuery.isSuccess || tagQuery.isError;
  // Wait for BOTH to settle before deciding — mirrors the original
  // Promise.allSettled semantics so a slow endpoint never flashes a
  // premature error/ready state.
  if (!linkSettled || !tagSettled) {
    return { status: 'loading', slug: '', bookingLink: '', retry };
  }

  if (linkQuery.isError) {
    const code = (linkQuery.error as { statusCode?: number })?.statusCode;
    return {
      status: code === 404 ? 'unavailable' : 'error',
      slug: '',
      bookingLink: '',
      retry,
    };
  }

  const link = linkQuery.data;
  // share_base_url is always present as a key but null when the org has no configured booking-link domain override (see get_booking_link_base_url), making this fallback a real, expected code path.
  const baseUrl = link.share_base_url ?? window.location.origin;
  // Embeds ALWAYS prefer the short-code URL (`/m/<code>/`): it is the canonical
  // public booking route in every environment, whereas the bare global-slug URL
  // (`/<slug>/`) can collide with app routes and — depending on the SPA router —
  // land an anonymous visitor on the app/login instead of the booking page. This
  // deliberately inverts the standalone `buildPreferredBookingUrl` ladder
  // (global_slug → short_code) because a snippet runs on arbitrary third-party
  // sites where robustness beats a prettier URL. Fall back to the global slug only
  // when no short code exists.
  const bookingLink =
    buildShortCodeUrl(baseUrl, link.short_code) ??
    buildGlobalSlugUrl(baseUrl, link.global_slug);
  if (!bookingLink) {
    return { status: 'unavailable', slug: '', bookingLink: '', retry };
  }

  if (tagQuery.isError || !tagQuery.data.slug) {
    return { status: 'error', slug: '', bookingLink: '', retry };
  }

  return { status: 'ready', slug: tagQuery.data.slug, bookingLink, retry };
}
